/**
 * AI Node Executors
 *
 * Handles execution of AI flow nodes:
 * - ai-transform: Transform code with AI
 * - ai-explain: Generate documentation
 * - ai-review: Code review with suggestions
 * - ai-chat: Interactive AI chat (uses MCP)
 *
 * These executors handle different context sources:
 * - flow_input: Get code from upstream node results
 * - specific_files: Load files matching pattern (handled by API)
 * - project_files: Load all project files (handled by API)
 * - mcp_tools: Use MCP chat endpoint
 */

import { getAuthHeaders } from './authHelper.js'

/**
 * Get artifacts_path from Code Analysis results in context
 */
function getArtifactsPathFromContext(context) {
  const results = context.results || {}
  console.log('[AI Executor] Looking for artifacts_path in context.results:', Object.keys(results))

  for (const nodeId of Object.keys(results)) {
    const result = results[nodeId]
    console.log(`[AI Executor] Node ${nodeId} result keys:`, result ? Object.keys(result) : 'null')
    console.log(`[AI Executor] Node ${nodeId} artifacts_path:`, result?.artifacts_path)
    // Code Analysis result has artifacts_path
    if (result?.artifacts_path) {
      console.log('[AI Executor] Found artifacts_path from node', nodeId, ':', result.artifacts_path)
      return result.artifacts_path
    }
  }
  console.log('[AI Executor] No artifacts_path found in any node result')
  return null
}

/**
 * Fetch generated Java files from the API
 * Uses artifacts_path from Code Analysis if available
 */
async function fetchGeneratedJavaFiles(context, signal) {
  const apiBaseUrl = context.api_base_url || 'http://localhost:8000'
  const account = context.scout_account_id
  const app = context.application_name

  // First, check if Code Analysis provided artifacts_path
  const artifactsPath = getArtifactsPathFromContext(context)

  if (artifactsPath) {
    console.log('[AI Executor] Using artifacts_path:', artifactsPath)
    // artifacts_path is like: 0U812/TestApp02/code_analysis/generated/ifpr321_cbl
    // We need to read Java files from this path
    return await fetchJavaFilesFromPath(apiBaseUrl, artifactsPath, signal)
  }

  // Fallback: try the /files/{account}/{app}/java endpoint
  console.log('[AI Executor] No artifacts_path found, trying /files API')
  return await fetchJavaFilesFromFilesAPI(apiBaseUrl, account, app, signal)
}

/**
 * Fetch Java files from a specific path using the storage API
 */
async function fetchJavaFilesFromPath(apiBaseUrl, artifactsPath, signal) {
  try {
    // artifacts_path is like: 0U812/TestApp02/code_analysis
    // Java files are in: 0U812/TestApp02/code_analysis/generated/{project_name}/
    // First, find the generated project folder
    const generatedPath = `${artifactsPath}/generated`
    console.log('[AI Executor] Looking for Java project in:', generatedPath)

    // List directories in generated folder
    const listDirsUrl = `${apiBaseUrl}/storage/list?path=${encodeURIComponent(generatedPath)}`
    const dirsResponse = await fetch(listDirsUrl, { signal })

    if (!dirsResponse.ok) {
      console.log('[AI Executor] Generated folder not found:', dirsResponse.status)
      return null
    }

    const dirsData = await dirsResponse.json()
    console.log('[AI Executor] Generated folder contents:', dirsData)

    // Find the project folder (first directory)
    const projectFolders = dirsData.files || []
    if (projectFolders.length === 0) {
      console.log('[AI Executor] No project folders found in generated/')
      return null
    }

    // Use the first project folder
    const projectPath = projectFolders[0].path || `${generatedPath}/${projectFolders[0].name}`
    console.log('[AI Executor] Using project path:', projectPath)

    // Now list Java files in the project folder
    const listUrl = `${apiBaseUrl}/storage/list?path=${encodeURIComponent(projectPath)}&pattern=*.java`
    console.log('[AI Executor] Listing Java files from:', listUrl)

    const listResponse = await fetch(listUrl, { signal })

    if (!listResponse.ok) {
      console.log('[AI Executor] Storage list failed:', listResponse.status)
      return null
    }

    const fileList = await listResponse.json()
    console.log('[AI Executor] Found files:', fileList)

    const files = fileList.files || fileList
    if (!files || files.length === 0) {
      console.log('[AI Executor] No Java files found in project path')
      return null
    }

    // Read content of each Java file (limit to first 10)
    const filesToRead = files.slice(0, 10)
    const fileContents = []

    for (const file of filesToRead) {
      const fileName = typeof file === 'string' ? file : file.name || file.path
      const filePath = fileName.includes('/') ? fileName : `${artifactsPath}/${fileName}`

      try {
        const readUrl = `${apiBaseUrl}/storage/read?path=${encodeURIComponent(filePath)}`
        const contentResponse = await fetch(readUrl, { signal })

        if (contentResponse.ok) {
          const contentData = await contentResponse.json()
          fileContents.push({
            name: fileName.split('/').pop(),
            content: contentData.content
          })
        }
      } catch (e) {
        console.warn(`Failed to read ${fileName}:`, e)
      }
    }

    if (fileContents.length === 0) {
      return null
    }

    return fileContents
      .map(f => `// ========== ${f.name} ==========\n${f.content}`)
      .join('\n\n')

  } catch (error) {
    console.error('[AI Executor] Failed to fetch from artifacts path:', error)
    return null
  }
}

/**
 * Fallback: Fetch Java files from /files/{account}/{app}/java endpoint
 */
async function fetchJavaFilesFromFilesAPI(apiBaseUrl, account, app, signal) {
  console.log('[AI Executor] Fetching Java files from:', `${apiBaseUrl}/files/${account}/${app}/java`)

  try {
    const listResponse = await fetch(
      `${apiBaseUrl}/files/${account}/${app}/java`,
      { signal }
    )

    console.log('[AI Executor] Java files list response:', listResponse.status, listResponse.ok)

    if (!listResponse.ok) {
      console.log('[AI Executor] No Java files available (response not ok)')
      return null
    }

    const fileList = await listResponse.json()
    console.log('[AI Executor] Java files list:', fileList)

    if (!fileList.files || fileList.files.length === 0) {
      console.log('[AI Executor] No Java files in list')
      return null
    }

    const filesToRead = fileList.files.slice(0, 10)
    const fileContents = []

    for (const file of filesToRead) {
      try {
        const contentResponse = await fetch(
          `${apiBaseUrl}/files/${account}/${app}/java/${file.name}`,
          { signal }
        )

        if (contentResponse.ok) {
          const contentData = await contentResponse.json()
          fileContents.push({
            name: file.name,
            content: contentData.content
          })
        }
      } catch (e) {
        console.warn(`Failed to read ${file.name}:`, e)
      }
    }

    if (fileContents.length === 0) {
      return null
    }

    return fileContents
      .map(f => `// ========== ${f.name} ==========\n${f.content}`)
      .join('\n\n')

  } catch (error) {
    console.error('[AI Executor] Failed to fetch Java files:', error)
    return null
  }
}

/**
 * Find code from upstream node results in context
 */
function getCodeFromContext(context) {
  const results = context.results || {}

  // Look through previous node results for code
  for (const nodeId of Object.keys(results).reverse()) {
    const result = results[nodeId]

    // Check common fields that might contain code
    if (result?.generated_code) return result.generated_code
    if (result?.code) return result.code
    if (result?.source_code) return result.source_code
    if (result?.transformed_code) return result.transformed_code

    // Check for file contents in artifacts
    if (result?.artifacts?.java_files) {
      // Combine Java files
      const javaFiles = result.artifacts.java_files
      if (Array.isArray(javaFiles) && javaFiles.length > 0) {
        return javaFiles.map(f => `// File: ${f.path || f.name}\n${f.content}`).join('\n\n')
      }
    }
  }

  return null
}

/**
 * Get analysis/structure data from upstream nodes
 */
function getAnalysisFromContext(context) {
  const results = context.results || {}

  for (const nodeId of Object.keys(results).reverse()) {
    const result = results[nodeId]

    if (result?.summary) return result.summary
    if (result?.analysis) return result.analysis
    if (result?.structure) return result.structure
  }

  return null
}

/**
 * Base AI executor - shared logic for all AI nodes
 */
async function executeAINode(nodeType, config, context, onProgress, signal) {
  const apiBaseUrl = context.api_base_url || 'http://localhost:8000'
  const contextSource = config.contextSource || 'flow_input'

  console.log(`[AI Executor] Starting ${nodeType}`, { contextSource, config })
  console.log('[AI Executor] Context:', {
    jobs: context.jobs,
    resultsKeys: Object.keys(context.results || {}),
    account: context.scout_account_id,
    app: context.application_name
  })

  onProgress(10, `Preparing ${nodeType} with ${contextSource} context...`)

  // Build request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    node_type: nodeType,
    context_source: contextSource,
    file_pattern: config.filePattern || null,
    provider: config.provider || 'bedrock',
    model: config.model || 'claude-sonnet-4-20250514',
    temperature: config.temperature || 0.2,
    max_tokens: config.maxTokens || 4096,
    config: config,
  }

  // Handle flow_input context source
  if (contextSource === 'flow_input') {
    onProgress(15, 'Looking for code from upstream nodes...')

    // First try to get code from context.results
    let code = getCodeFromContext(context)
    console.log('[AI Executor] Code from context.results:', code ? `${code.length} chars` : 'null')

    // If no code in context, check if Code Analysis ran and fetch Java files
    if (!code) {
      const hasCodeAnalysis = context.jobs?.codeanalysis_job_id
      console.log('[AI Executor] codeanalysis_job_id:', hasCodeAnalysis)
      if (hasCodeAnalysis) {
        onProgress(20, 'Fetching generated Java files...')
        code = await fetchGeneratedJavaFiles(context, signal)
        console.log('[AI Executor] Fetched Java files:', code ? `${code.length} chars` : 'null')
      }
    }

    if (!code) {
      const debugInfo = {
        hasCodeAnalysisJob: !!context.jobs?.codeanalysis_job_id,
        jobId: context.jobs?.codeanalysis_job_id || 'none',
        resultsKeys: Object.keys(context.results || {}),
      }
      console.log('[AI Executor] No code found, debug info:', debugInfo)
      return {
        success: false,
        error: `No code found. Code Analysis job: ${debugInfo.jobId}. Try changing Context Source to "Specific Files" with pattern "*.java".`,
        node_type: nodeType,
      }
    }
    requestBody.code = code
    requestBody.context = getAnalysisFromContext(context)
    console.log('[AI Executor] Code added to request:', code.length, 'chars')
  }

  onProgress(30, `Calling AI ${nodeType}...`)

  try {
    // For MCP tools, use the MCP chat endpoint
    if (contextSource === 'mcp_tools') {
      return await executeMCPChat(config, context, onProgress, signal)
    }

    // Call the AI flow endpoint
    const response = await fetch(`${apiBaseUrl}/ai/flow`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `API error: ${response.status}`)
    }

    const result = await response.json()
    console.log('[AI Executor] API Response:', {
      success: result.success,
      hasOutput: !!result.output,
      outputKeys: result.output ? Object.keys(result.output) : [],
      error: result.error
    })

    // Show preview of result in progress
    if (nodeType === 'ai-explain' && result.output?.documentation) {
      const preview = result.output.documentation.substring(0, 500)
      onProgress(100, `Documentation generated:\n\n${preview}${result.output.documentation.length > 500 ? '...' : ''}`)
    } else if (nodeType === 'ai-transform' && result.output?.transformed_code) {
      const lineCount = result.output.transformed_code.split('\n').length
      onProgress(100, `Transformation complete: ${lineCount} lines of code generated`)
    } else if (nodeType === 'ai-review' && result.output?.review) {
      const findingCount = result.output.review.findings?.length || 0
      onProgress(100, `Review complete: ${findingCount} findings\n\nSummary: ${result.output.review.summary || 'See results'}`)
    } else {
      onProgress(100, `${nodeType} complete`)
    }

    const finalResult = {
      success: result.success,
      node_type: nodeType,
      output: result.output,
      files_analyzed: result.files_analyzed || [],
      duration_ms: result.duration_ms,
      context_source: contextSource,
      error: result.error,

      // Expose specific outputs for downstream nodes
      ...(nodeType === 'ai-transform' && result.output ? {
        transformed_code: result.output.transformed_code,
        code: result.output.transformed_code,  // alias
      } : {}),
      ...(nodeType === 'ai-explain' && result.output ? {
        documentation: result.output.documentation,
      } : {}),
      ...(nodeType === 'ai-review' && result.output ? {
        review: result.output.review,
      } : {}),
    }

    console.log('[AI Executor] Returning result:', {
      success: finalResult.success,
      hasDocumentation: !!finalResult.documentation,
      hasTransformed: !!finalResult.transformed_code,
      hasReview: !!finalResult.review
    })

    return finalResult

  } catch (error) {
    console.error('[AI Executor] Error:', error)
    if (error.name === 'AbortError') {
      return { success: false, error: 'Execution aborted', node_type: nodeType }
    }
    return { success: false, error: error.message, node_type: nodeType }
  }
}

/**
 * Execute MCP Chat for ai-chat node with mcp_tools context
 */
async function executeMCPChat(config, context, onProgress, signal) {
  const apiBaseUrl = context.api_base_url || 'http://localhost:8000'

  // Check if MCP is running
  onProgress(20, 'Checking MCP server status...')

  try {
    const statusResponse = await fetch(
      `${apiBaseUrl}/mcp/running/${context.scout_account_id}/${context.application_name}`,
      { signal }
    )
    const status = await statusResponse.json()

    if (!status.running) {
      return {
        success: false,
        error: 'MCP server is not running. Generate and start MCP first, or change Context Source.',
        node_type: 'ai-chat',
      }
    }

    onProgress(40, 'Sending message to MCP chat...')

    // Get the message from config
    const message = config.message || 'What can you tell me about this codebase?'

    // Call MCP chat endpoint
    const response = await fetch(`${apiBaseUrl}/ai/mcp-chat`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        scout_account_id: context.scout_account_id,
        application_name: context.application_name,
        message: message,
        history: [],  // Fresh conversation for flow execution
      }),
      signal,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `MCP chat error: ${response.status}`)
    }

    const result = await response.json()

    onProgress(100, 'MCP chat complete')

    return {
      success: true,
      node_type: 'ai-chat',
      response: result.response,
      tools_used: result.tools_used || [],
      duration_ms: result.duration_ms,
      context_source: 'mcp_tools',
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      return { success: false, error: 'Execution aborted', node_type: 'ai-chat' }
    }
    return { success: false, error: error.message, node_type: 'ai-chat' }
  }
}

// =============================================================================
// Exported Executors
// =============================================================================

export const aiTransformExecutor = {
  async execute(config, context, onProgress = () => {}, signal = null) {
    return executeAINode('ai-transform', config, context, onProgress, signal)
  }
}

export const aiExplainExecutor = {
  async execute(config, context, onProgress = () => {}, signal = null) {
    return executeAINode('ai-explain', config, context, onProgress, signal)
  }
}

export const aiReviewExecutor = {
  async execute(config, context, onProgress = () => {}, signal = null) {
    return executeAINode('ai-review', config, context, onProgress, signal)
  }
}

export const aiChatExecutor = {
  async execute(config, context, onProgress = () => {}, signal = null) {
    const contextSource = config.contextSource || 'mcp_tools'

    // MCP tools uses special flow
    if (contextSource === 'mcp_tools') {
      return executeMCPChat(config, context, onProgress, signal)
    }

    // Otherwise use standard AI flow
    return executeAINode('ai-chat', config, context, onProgress, signal)
  }
}
